import { useMemo, useState } from 'react';
import { Check, Inbox, Mail, UserPlus, X } from 'lucide-react';
import type { AdminAccessRequestRecord, AdminSnapshot } from '../types';

interface AccessRequestsFeatureProps {
  readonly snapshot: AdminSnapshot;
  readonly canDecide: boolean;
  readonly busy: boolean;
  readonly onDecide: (requestId: string, approve: boolean, note: string) => Promise<void>;
}

const statusLabels: Record<AdminAccessRequestRecord['status'], string> = {
  pending: 'Offen',
  approved: 'Freigegeben',
  rejected: 'Abgelehnt',
};

function dateLabel(value: string): string {
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

/**
 * Zugangsanfragen aus dem oeffentlichen Login-Formular.
 *
 * Eine Freigabe legt BEWUSST keinen Benutzer an -- sie ist nur die Entscheidung "der darf".
 * Danach folgt die normale Einladung unter "Benutzer", mit bewusst gewaehlten Rollen. Ein Zugang
 * ohne Rollenentscheidung waere ein Zugang, den niemand vergeben hat. Deshalb steht nach dem
 * Freigeben ausdruecklich dran, dass noch etwas zu tun ist.
 */
export function AccessRequestsFeature({ snapshot, canDecide, busy, onDecide }: AccessRequestsFeatureProps) {
  const [notes, setNotes] = useState<Record<string, string>>({});

  const { open, decided } = useMemo(() => ({
    open: snapshot.accessRequests.filter((request) => request.status === 'pending'),
    decided: snapshot.accessRequests.filter((request) => request.status !== 'pending'),
  }), [snapshot.accessRequests]);

  return (
    <section className="admin-requests">
      <header className="admin-requests__head">
        <div>
          <h2>Zugangsanfragen</h2>
          <p>
            Anfragen über das Login-Formular. Angenommen werden nur Adressen der hinterlegten
            Firmendomains – alles andere weist der Server ab, bevor es hier ankommt.
          </p>
        </div>
        <span className={`admin-requests__count${open.length ? ' is-open' : ''}`}>
          <Inbox size={14} /> {open.length} offen
        </span>
      </header>

      {open.length === 0 ? (
        <p className="admin-empty">Keine offenen Anfragen.</p>
      ) : (
        <ul className="admin-requests__list">
          {open.map((request) => (
            <li key={request.id} className="admin-requests__item">
              <div className="admin-requests__who">
                <strong>{request.displayName}</strong>
                <a href={`mailto:${request.email}`}><Mail size={12} /> {request.email}</a>
                <small>angefragt {dateLabel(request.createdAt)}</small>
              </div>
              {request.note && <p className="admin-requests__note">„{request.note}"</p>}
              {canDecide && (
                <div className="admin-requests__actions">
                  <input
                    value={notes[request.id] ?? ''}
                    placeholder="Vermerk (optional)"
                    onChange={(event) => setNotes((current) => ({ ...current, [request.id]: event.target.value }))}
                  />
                  <button
                    type="button"
                    className="admin-requests__approve"
                    disabled={busy}
                    onClick={() => void onDecide(request.id, true, notes[request.id] ?? '')}
                  >
                    <Check size={13} /> Freigeben
                  </button>
                  <button
                    type="button"
                    className="admin-requests__reject"
                    disabled={busy}
                    onClick={() => void onDecide(request.id, false, notes[request.id] ?? '')}
                  >
                    <X size={13} /> Ablehnen
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {open.length > 0 && canDecide && (
        <p className="admin-requests__hint">
          <UserPlus size={13} />
          <span>
            <strong>Freigeben legt noch kein Konto an.</strong> Es ist die Entscheidung „darf" –
            den Zugang vergibst du danach unter <em>Benutzer</em> per Einladung, mit den Rollen,
            die die Person wirklich braucht.
          </span>
        </p>
      )}

      {decided.length > 0 && (
        <>
          <h3 className="admin-requests__subhead">Entschieden</h3>
          <div className="admin-requests__table-wrap">
            <table className="admin-requests__table">
              <thead>
                <tr>
                  <th scope="col">Name</th><th scope="col">E-Mail</th><th scope="col">Status</th>
                  <th scope="col">Entschieden</th><th scope="col">Von</th><th scope="col">Vermerk</th>
                </tr>
              </thead>
              <tbody>
                {decided.map((request) => (
                  <tr key={request.id}>
                    <td>{request.displayName}</td>
                    <td>{request.email}</td>
                    <td>
                      <span className={`admin-requests__pill is-${request.status}`}>
                        {statusLabels[request.status]}
                      </span>
                    </td>
                    <td>{request.decidedAt ? dateLabel(request.decidedAt) : '–'}</td>
                    <td>{request.decidedByName ?? '–'}</td>
                    <td>{request.decisionNote || '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
